import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, mediaUrl, formatMoney } from '@/lib/api';
import { useCategories } from '@/context/CategoriesContext';
import { PublicHeader } from '@/components/marketplace/PublicHeader';
import { AdCard } from '@/components/marketplace/AdCard';
import { RatingStars } from '@/components/marketplace/RatingStars';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Search, ArrowRight, ShieldCheck, Zap, HeartHandshake, ChevronLeft, ChevronRight, Users, Package as PackageIcon, Star, Gift, Gavel, MapPin, Loader2 } from 'lucide-react';
import { FlashSaleCarousel } from '@/components/marketplace/FlashSaleCarousel';
import { LiveNowCarousel } from '@/components/marketplace/LiveNowCarousel';

// Hero carousel with auto-advance + big search bar with GO button
const HeroCarousel = ({ images }) => {
    const [idx, setIdx] = useState(0);
    const [q, setQ] = useState('');
    const nav = useNavigate();

    useEffect(() => {
        if (!images.length) return;
        const t = setInterval(() => setIdx(i => (i + 1) % images.length), 5000);
        return () => clearInterval(t);
    }, [images.length]);

    const submit = (e) => {
        e.preventDefault();
        const t = q.trim();
        nav(t ? `/search?q=${encodeURIComponent(t)}` : '/search');
    };

    return (
        <section className="relative w-full overflow-hidden bg-black" data-testid="home-hero">
            <div className="relative h-[60vh] min-h-[420px] max-h-[720px]">
                {images.map((src, i) => (
                    <div key={i} className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${i === idx ? 'opacity-100' : 'opacity-0'}`}
                         style={{ backgroundImage: `url(${src})` }} />
                ))}
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/70" />

                <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 sm:px-6 lg:px-10 text-white text-center">
                    {/* Brand logo with illuminated glow */}
                    <div className="mb-5 relative">
                        <div className="absolute inset-0 rounded-2xl blur-2xl opacity-40 bg-white scale-110" />
                        <div className="relative rounded-2xl bg-white/15 backdrop-blur-sm border border-white/25 px-6 py-3 shadow-2xl"
                            style={{ boxShadow: '0 0 32px rgba(255,255,255,0.2), 0 0 64px rgba(255,255,255,0.08)' }}>
                            <img src="/assets/oktelovendoligth.png" alt="Ok te lo Vendo" className="h-10 sm:h-14 w-auto" />
                        </div>
                    </div>
                    <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl drop-shadow-lg">Vende lo que ya no uses y compra lo que más te guste.</h1>
                    <p className="mt-3 text-base sm:text-lg text-white/90 max-w-2xl">En Ok te lo Vendo. Publica en segundos. Recibe pagos protegidos.</p>

                    {/* Big search bar with GO */}
                    <form onSubmit={submit} className="mt-8 w-full max-w-3xl">
                        <div className="flex items-center rounded-full bg-white shadow-2xl overflow-hidden pl-5 pr-1.5 py-1.5">
                            <Search className="h-5 w-5 text-neutral-500 shrink-0" />
                            <input
                                autoFocus
                                value={q} onChange={(e) => setQ(e.target.value)}
                                placeholder="Buscar en Ok te lo vendo..."
                                className="flex-1 h-12 px-3 text-neutral-900 text-base sm:text-lg bg-transparent outline-none"
                                data-testid="hero-search-input"
                            />
                            <button type="submit" data-testid="hero-go-button" className="h-12 px-6 sm:px-8 rounded-full bg-red-600 hover:bg-red-700 text-white font-bold text-lg tracking-wide transition-colors">GO</button>
                        </div>
                    </form>

                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {images.map((_, i) => (
                            <button key={i} onClick={() => setIdx(i)} className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-8 bg-red-600' : 'w-2 bg-white/50 hover:bg-white'}`} />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

// Full-width feature banners (3)
const FeatureBanners = () => (
    <section className="grid grid-cols-1 md:grid-cols-3" data-testid="feature-banners">
        {[
            { icon: ShieldCheck, title: 'Pago protegido', desc: 'Escrow por Stripe: liberamos al vendedor cuando confirmas recepción.', img: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&h=500&fit=crop&auto=format&q=80', bg: 'bg-red-600' },
            { icon: Zap, title: 'Publicación rápida', desc: 'Wizard intuitivo con fotos, videos y variantes. Publica en minutos.', img: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=500&fit=crop&auto=format&q=80', bg: 'bg-black' },
            { icon: HeartHandshake, title: 'Reputación real', desc: 'Calificación mutua 1-5 estrellas + reseñas de productos verificadas.', img: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=500&fit=crop&auto=format&q=80', bg: 'bg-red-600' },
        ].map((f, i) => (
            <div key={i} className="relative min-h-[240px] md:min-h-[300px] overflow-hidden group">
                <img src={f.img} alt="" className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className={`absolute inset-0 ${f.bg} opacity-80 group-hover:opacity-90 transition-opacity`} />
                <div className="relative h-full p-6 flex flex-col justify-end text-white">
                    <f.icon className="h-8 w-8 mb-2" />
                    <div className="text-xl sm:text-2xl font-bold">{f.title}</div>
                    <div className="text-sm mt-1 text-white/90">{f.desc}</div>
                </div>
            </div>
        ))}
    </section>
);

// Categories horizontal carousel
export const CategoriesGrid = ({ cats, compact = false }) => (
    <section className={compact ? "" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10"}>
        {!compact && (
            <div className="flex items-baseline justify-between mb-5">
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Explora por categoría</h2>
                <Link to="/search" className="text-sm text-red-600 hover:underline">Ver todo <ArrowRight className="h-3 w-3 inline" /></Link>
            </div>
        )}
        <div className={`flex gap-3 overflow-x-auto snap-x snap-mandatory pb-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden ${compact ? '' : '-mx-1 px-1'}`}>
            {cats.map(c => (
                <Link key={c.slug} to={`/categories/${c.slug}`} data-testid={`cat-card-${c.slug}`}
                      className={`group relative rounded-2xl overflow-hidden snap-start shrink-0 shadow-sm hover:shadow-xl transition-all ${
                          compact ? 'w-32 sm:w-40 h-20 sm:h-24' : 'w-40 sm:w-52 md:w-60 h-28 sm:h-36 md:h-40'
                      }`}>
                    <div className="absolute inset-0 bg-cover bg-center group-hover:scale-110 transition-transform duration-500" style={{ backgroundImage: `url(${c.image_url})` }} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                    <div className="absolute inset-0 flex flex-col justify-end p-2 sm:p-4 text-white">
                        <div className={`font-bold drop-shadow-lg ${compact ? 'text-xs' : 'text-sm sm:text-base'}`}>{c.name}</div>
                        {!compact && <div className="text-[10px] text-white/75 mt-0.5">{c.ads_count || 0} anuncios</div>}
                    </div>
                    <div className="absolute top-2 right-2 h-1.5 w-1.5 rounded-full bg-red-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
            ))}
        </div>
    </section>
);

// Banner carousel (auto-advance)
const BannerCarousel = ({ banners, testId = 'banner-carousel' }) => {
    const [idx, setIdx] = useState(0);
    useEffect(() => {
        if (banners.length < 2) return;
        const t = setInterval(() => setIdx(i => (i + 1) % banners.length), 6000);
        return () => clearInterval(t);
    }, [banners.length]);

    if (!banners.length) return null;

    return (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6" data-testid={testId}>
            <div className="relative rounded-2xl overflow-hidden shadow-lg">
                <div className="relative h-48 sm:h-64 md:h-80">
                    {banners.map((b, i) => (
                        <Link key={b.id} to={b.link_url || '#'} className={`absolute inset-0 transition-opacity duration-700 ${i === idx ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                            <img src={b.image_url} alt={b.title || ''} className="absolute inset-0 h-full w-full object-cover" />
                            <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${b.bg_color || '#dc2626'}dd 0%, transparent 60%)` }} />
                            <div className="relative h-full flex flex-col justify-center p-6 sm:p-10 text-white max-w-xl">
                                <div className="text-2xl sm:text-4xl font-bold drop-shadow-lg">{b.title}</div>
                                {b.subtitle && <div className="mt-2 text-sm sm:text-base text-white/90">{b.subtitle}</div>}
                                {b.cta_text && (
                                    <div className="mt-4"><span className="inline-flex items-center gap-2 bg-white text-black rounded-full px-5 py-2 font-semibold text-sm shadow-lg">{b.cta_text}<ArrowRight className="h-4 w-4" /></span></div>
                                )}
                            </div>
                        </Link>
                    ))}
                </div>
                {banners.length > 1 && (
                    <div className="absolute bottom-3 right-3 flex gap-1.5">
                        {banners.map((_, i) => (
                            <button key={i} onClick={() => setIdx(i)} className={`h-1.5 rounded-full ${i === idx ? 'w-6 bg-white' : 'w-1.5 bg-white/50'}`} />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

// Users showcase (new + popular) — redesigned: card-with-cover format
const SELLER_GRADIENTS = [
    'from-rose-500 via-red-600 to-orange-500',
    'from-red-700 via-rose-600 to-pink-500',
    'from-slate-800 via-red-900 to-red-700',
    'from-neutral-900 via-red-800 to-rose-600',
    'from-red-600 via-pink-600 to-rose-700',
    'from-orange-600 via-red-600 to-rose-600',
    'from-slate-900 via-red-700 to-orange-600',
    'from-rose-700 via-red-700 to-red-500',
];
const UsersShowcase = ({ title, users, testId }) => (
    <div className="space-y-5" data-testid={testId}>
        <h3 className="text-lg sm:text-xl font-semibold tracking-tight">{title}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {users.map((u, i) => {
                const grad = SELLER_GRADIENTS[i % SELLER_GRADIENTS.length];
                return (
                    <Link
                        key={u.id}
                        to={`/u/${u.nickname}`}
                        className="group relative rounded-2xl overflow-hidden bg-white border border-neutral-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col"
                    >
                        {/* Cover gradient with shimmer overlay */}
                        <div className={`h-[72px] bg-gradient-to-br ${grad} relative overflow-hidden shrink-0`}>
                            <div className="absolute inset-0 opacity-25 bg-[radial-gradient(ellipse_at_20%_50%,white,transparent_55%)]" />
                            <div className="absolute inset-0 opacity-10 bg-[repeating-linear-gradient(45deg,white,white_1px,transparent_1px,transparent_8px)]" />
                        </div>

                        {/* Avatar overlapping cover */}
                        <div className="px-4 -mt-7 relative z-10 flex items-end justify-between">
                            <div className="h-[52px] w-[52px] rounded-xl ring-[3px] ring-white shadow-lg overflow-hidden bg-white shrink-0">
                                {u.avatar_url
                                    ? <img src={u.avatar_url} alt="" className="h-full w-full object-cover" />
                                    : <div className={`h-full w-full bg-gradient-to-br ${grad} flex items-center justify-center font-bold text-white text-lg`}>{u.nickname[0].toUpperCase()}</div>
                                }
                            </div>
                            {u.rating_avg >= 4.5 && (
                                <span className="mb-1 inline-flex items-center gap-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide shrink-0">
                                    <Star className="h-2.5 w-2.5 fill-amber-500 text-amber-500" /> Top
                                </span>
                            )}
                        </div>

                        {/* Info body */}
                        <div className="px-4 pt-2 pb-4 flex flex-col flex-1 min-w-0">
                            <div className="font-bold text-sm truncate text-neutral-900 group-hover:text-red-600 transition-colors leading-tight">@{u.nickname}</div>
                            <div className="text-[11px] text-neutral-400 truncate mt-0.5 flex items-center gap-0.5">
                                <MapPin className="h-2.5 w-2.5 shrink-0" />
                                <span className="truncate">{u.location || 'México'}</span>
                            </div>

                            {/* Stats mini-grid */}
                            <div className="mt-3 grid grid-cols-3 divide-x divide-neutral-100 text-center rounded-xl bg-neutral-50 border border-neutral-100 overflow-hidden">
                                <div className="py-2 flex flex-col gap-0.5">
                                    <span className="text-xs font-bold text-neutral-900 leading-none tabular-nums">{u.rating_avg?.toFixed(1) || '—'}</span>
                                    <span className="text-[9px] text-neutral-400 uppercase tracking-wide leading-none">Rating</span>
                                </div>
                                <div className="py-2 flex flex-col gap-0.5">
                                    <span className="text-xs font-bold text-neutral-900 leading-none tabular-nums">{u.ads_count}</span>
                                    <span className="text-[9px] text-neutral-400 uppercase tracking-wide leading-none">Anuncios</span>
                                </div>
                                <div className="py-2 flex flex-col gap-0.5">
                                    <span className="text-xs font-bold text-neutral-900 leading-none tabular-nums">{u.followers_count || 0}</span>
                                    <span className="text-[9px] text-neutral-400 uppercase tracking-wide leading-none">Seguidores</span>
                                </div>
                            </div>

                            {/* Ver tienda CTA */}
                            <div className="mt-3 text-[11px] font-semibold text-red-600 group-hover:text-red-700 flex items-center gap-0.5 transition-colors">
                                Ver tienda <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                            </div>
                        </div>
                    </Link>
                );
            })}
        </div>
    </div>
);

export default function HomePage() {
    const cats = useCategories();
    const [ads, setAds] = useState([]);
    const [heroImages, setHeroImages] = useState([]);
    const [banners1, setBanners1] = useState([]);
    const [banners2, setBanners2] = useState([]);
    const [newUsers, setNewUsers] = useState([]);
    const [popularUsers, setPopularUsers] = useState([]);
    const [cashbackAds, setCashbackAds] = useState([]);
    const [endingAuctions, setEndingAuctions] = useState([]);
    const [geoAds, setGeoAds] = useState(null);  // null=off, [] loading, [items]
    const [geoStatus, setGeoStatus] = useState('idle'); // idle | requesting | ok | denied | unsupported
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [a, s, b1, b2, uN, uP, cb, au] = await Promise.all([
                    api.get('/ads', { params: { limit: 12, sort: 'recent' } }),
                    api.get('/public/settings'),
                    api.get('/banners', { params: { kind: 'promo1' } }),
                    api.get('/banners', { params: { kind: 'promo2' } }),
                    api.get('/featured-users', { params: { kind: 'new', limit: 8 } }),
                    api.get('/featured-users', { params: { kind: 'popular', limit: 8 } }),
                    api.get('/ads', { params: { limit: 8, with_cashback: true } }),
                    api.get('/ads', { params: { limit: 8, auction_ending: true } }),
                ]);
                setAds(a.data.items);
                setCashbackAds(cb.data.items || []);
                setEndingAuctions(au.data.items || []);
                setHeroImages(s.data.hero_images || []);
                setBanners1(b1.data);
                setBanners2(b2.data);
                setNewUsers(uN.data);
                setPopularUsers(uP.data);
            } finally { setLoading(false); }
        })();
    }, []);

    return (
        <div className="min-h-screen flex flex-col">
            <PublicHeader />
            <main className="flex-1">
                {/* HERO with search */}
                {heroImages.length > 0 && <HeroCarousel images={heroImages} />}

                {/* Categories large grid */}
                <CategoriesGrid cats={cats} />

                {/* Flash sales / Deal of the day */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
                    <LiveNowCarousel />
                    <FlashSaleCarousel />
                </div>

                {/* Services promo banner removed by request (Phase 20f) */}

                {/* Recent ads */}
                <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6">
                    <div className="flex items-baseline justify-between mb-5">
                        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Recomendados para ti</h2>
                        <Link to="/search" className="text-sm text-red-600 hover:underline">Ver todo <ArrowRight className="h-3 w-3 inline" /></Link>
                    </div>
                    {loading ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
                            {[...Array(8)].map((_, i) => <Skeleton key={i} className="aspect-[4/5] rounded-2xl" />)}
                        </div>
                    ) : ads.length === 0 ? (
                        <div className="text-center py-20 text-muted-foreground">Aún no hay anuncios.</div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5" data-testid="home-ads-grid">
                            {ads.map(a => <AdCard key={a.id} ad={a} />)}
                        </div>
                    )}
                </section>

                {/* Cashback ads section */}
                <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8" data-testid="home-section-cashback">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white flex items-center justify-center shadow-md">
                                <Gift className="h-5 w-5" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold tracking-tight">Los que te dan cashback</h2>
                                <p className="text-xs text-muted-foreground">Recupera dinero al comprar estos productos.</p>
                            </div>
                        </div>
                        <Link to="/search?with_cashback=true" className="text-sm text-emerald-700 hover:underline font-semibold">Ver todo <ArrowRight className="h-3 w-3 inline" /></Link>
                    </div>
                    {loading ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
                            {[...Array(4)].map((_, i) => <Skeleton key={i} className="aspect-[4/5] rounded-2xl" />)}
                        </div>
                    ) : cashbackAds.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-emerald-300 bg-emerald-50/50 py-10 text-center text-sm text-emerald-700">
                            Pronto habrá anuncios con cashback aquí. ¡Activa cashback en tu anuncio!
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5" data-testid="home-cashback-grid">
                            {cashbackAds.map(a => <AdCard key={a.id} ad={a} />)}
                        </div>
                    )}
                </section>

                {/* Auctions ending soon */}
                <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8" data-testid="home-section-auctions">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 text-white flex items-center justify-center shadow-md">
                                <Gavel className="h-5 w-5" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold tracking-tight">Las subastas que terminan</h2>
                                <p className="text-xs text-muted-foreground">Puja antes de que se cierre el cronómetro.</p>
                            </div>
                        </div>
                        <Link to="/search?auction_ending=true" className="text-sm text-red-700 hover:underline font-semibold">Ver todas <ArrowRight className="h-3 w-3 inline" /></Link>
                    </div>
                    {loading ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
                            {[...Array(4)].map((_, i) => <Skeleton key={i} className="aspect-[4/5] rounded-2xl" />)}
                        </div>
                    ) : endingAuctions.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-red-300 bg-red-50/50 py-10 text-center text-sm text-red-700">
                            No hay subastas activas por terminar en este momento. ¡Vuelve pronto!
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5" data-testid="home-auctions-grid">
                            {endingAuctions.map(a => <AdCard key={a.id} ad={a} />)}
                        </div>
                    )}
                </section>

                {/* Phase 20i: Geolocation "near me" */}
                <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6" data-testid="home-section-geo">
                    <div className="rounded-2xl bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 text-white p-5 sm:p-6 flex flex-col sm:flex-row items-center gap-4 shadow-lg">
                        <div className="flex-1 text-center sm:text-left">
                            <h3 className="text-xl sm:text-2xl font-bold flex items-center justify-center sm:justify-start gap-2"><MapPin className="h-5 w-5" />Anuncios cerca de ti</h3>
                            <p className="text-white/85 text-sm mt-1">Encuentra ofertas en tu ciudad o colonia.</p>
                        </div>
                        <button
                            onClick={() => {
                                if (!navigator.geolocation) { setGeoStatus('unsupported'); return; }
                                setGeoStatus('requesting'); setGeoAds([]);
                                navigator.geolocation.getCurrentPosition(
                                    (pos) => {
                                        api.get('/ads', { params: { near_lat: pos.coords.latitude, near_lng: pos.coords.longitude, radius_km: 25, limit: 8 } })
                                            .then(({ data }) => { setGeoAds(data.items || []); setGeoStatus('ok'); })
                                            .catch(() => setGeoStatus('denied'));
                                    },
                                    () => setGeoStatus('denied'),
                                    { enableHighAccuracy: true, timeout: 10000 },
                                );
                            }}
                            className="rounded-xl bg-white text-blue-700 hover:bg-blue-50 px-5 py-2.5 font-semibold shadow flex items-center gap-2 transition-colors"
                            data-testid="home-geo-btn"
                        >
                            {geoStatus === 'requesting' ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
                            {geoStatus === 'requesting' ? 'Buscando…' : 'Usar mi ubicación'}
                        </button>
                    </div>
                    {geoStatus === 'denied' && <div className="text-xs text-muted-foreground mt-2 text-center">No pudimos obtener tu ubicación. Habilita permisos en tu navegador.</div>}
                    {geoStatus === 'unsupported' && <div className="text-xs text-muted-foreground mt-2 text-center">Tu navegador no soporta geolocalización.</div>}
                    {geoStatus === 'ok' && (
                        <div className="mt-4" data-testid="home-geo-results">
                            {geoAds.length === 0 ? (
                                <div className="text-center text-sm text-muted-foreground py-6">No hay anuncios en 25 km. Prueba ampliar el radio en la búsqueda.</div>
                            ) : (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
                                    {geoAds.map(a => <AdCard key={a.id} ad={a} />)}
                                </div>
                            )}
                        </div>
                    )}
                </section>

                {/* Promo banner carousel #1 */}
                <BannerCarousel banners={banners1} testId="banner-carousel-1" />

                {/* Users showcase */}
                <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 space-y-8">
                    <UsersShowcase title="🆕 Nuevos vendedores" users={newUsers.slice(0, 8)} testId="users-new" />
                    <UsersShowcase title="⭐ Vendedores populares" users={popularUsers.slice(0, 8)} testId="users-popular" />
                </section>

                {/* Promo banner carousel #2 */}
                <BannerCarousel banners={banners2} testId="banner-carousel-2" />

                {/* Feature banners full width - moved to the end */}
                <FeatureBanners />

                <div className="h-16" />
            </main>
        </div>
    );
}
